import { API, API_BASE, toFullUrl } from '../config/api';
import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { Play, Pause, Download, Music, ArrowLeft, ListPlus } from 'lucide-react';
import { usePlayer } from '../context/PlayerContext';
import CommentSection from '../components/CommentSection';
import LikeButton from '../components/LikeButton';
import FireButton from '../components/FireButton';
import { Button } from '../components/ui/button';
import { toast } from 'sonner';

export default function FreeMediaPage() {
  const [freeMedia, setFreeMedia] = useState([]);
  const [siteSettings, setSiteSettings] = useState({});
  const { currentTrack, isPlaying, playTrack, addToQueue } = usePlayer();

  useEffect(() => {
    Promise.all([
      axios.get(`${API}/free-media`).catch(() => ({ data: [] })),
      axios.get(`${API}/site-settings`).catch(() => ({ data: {} }))
    ]).then(([mediaRes, settingsRes]) => {
      setFreeMedia(mediaRes.data);
      setSiteSettings(settingsRes.data);
    });
  }, []);

  const playFreeMedia = (item) => {
    if (!item.audio_file) {
      toast.error('No audio uploaded for this track yet.');
      return;
    }
    playTrack({ ...item, preview_url: `${API_BASE}/api/files/${item.audio_file}`, id: item.id });
  };

  const queueFreeMedia = (item) => {
    if (!item.audio_file) {
      toast.error('No audio uploaded for this track yet.');
      return;
    }
    const ok = addToQueue({ ...item, preview_url: `${API_BASE}/api/files/${item.audio_file}`, id: item.id });
    toast[ok ? 'success' : 'message'](ok ? `Added "${item.title}" to queue` : 'Already in queue');
  };

  const downloadFreeMedia = (item) => {
    window.open(`${API_BASE}/api/free-media/${item.id}/download`, '_blank');
  };

  // Group into albums and singles
  const albumGroups = {};
  const singles = [];
  freeMedia.forEach(item => {
    if (item.album_name) {
      if (!albumGroups[item.album_name]) albumGroups[item.album_name] = [];
      albumGroups[item.album_name].push(item);
    } else {
      singles.push(item);
    }
  });
  Object.keys(albumGroups).forEach(k => albumGroups[k].sort((a, b) => (a.track_number || 0) - (b.track_number || 0)));
  const sortedAlbumNames = Object.keys(albumGroups).sort((a, b) => {
    const yearA = albumGroups[a][0]?.release_date || '9999';
    const yearB = albumGroups[b][0]?.release_date || '9999';
    if (yearA !== yearB) return yearA.localeCompare(yearB);
    const orderA = albumGroups[a][0]?.album_order ?? 999;
    const orderB = albumGroups[b][0]?.album_order ?? 999;
    return orderA - orderB;
  });

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-8 py-12">
        <div className="flex items-center gap-4 mb-10">
          <Link to="/" data-testid="back-home-link">
            <Button variant="outline" className="rounded-none border-[#262626] hover:border-[#DAA520]">
              <ArrowLeft className="w-4 h-4 mr-2" /> BACK
            </Button>
          </Link>
          <div>
            <p className="overline text-[#FF3B30] mb-1 text-xs tracking-widest font-bold">FREE</p>
            <h1 className="font-cabinet text-3xl sm:text-4xl font-black uppercase tracking-tighter" data-testid="free-media-page-title">
              {siteSettings?.section_titles?.free_media || 'FREE MEDIA'}
            </h1>
          </div>
        </div>

        <div className="space-y-8">
          {/* Featured hero */}
          {(() => {
            const featured = freeMedia.filter(m => m.home_featured).sort((a,b)=>(a.home_featured_order??0)-(b.home_featured_order??0)).slice(0, 6);
            if (featured.length === 0) return null;
            return (
              <div data-testid="media-featured-hero">
                <p className="overline text-[#DAA520] mb-3 text-[10px] tracking-widest font-bold">★ FEATURED</p>
                <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
                  {featured.map(m => (
                    <div key={`hero-${m.id}`} className="aspect-square bg-[#0A0A0A] border border-[#DAA520]/30 hover:border-[#DAA520] overflow-hidden relative" data-testid={`featured-media-${m.id}`}>
                      {m.cover_image
                        ? <img src={toFullUrl(m.cover_image)} alt={m.title} className="w-full h-full object-cover" />
                        : <div className="w-full h-full flex items-center justify-center text-[#262626] text-xs p-2 text-center">{m.title}</div>}
                      <div className="absolute inset-x-0 bottom-0 bg-gradient-to-t from-black via-black/70 to-transparent p-2">
                        <p className="text-[11px] font-bold text-white truncate">{m.title}</p>
                        <p className="text-[9px] text-[#A3A3A3] truncate">{m.artist || ''}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            );
          })()}

          {/* Albums */}
          {sortedAlbumNames.map((albumName, idx) => {
            const tracks = albumGroups[albumName];
            const year = tracks[0]?.release_date || '';
            return (
              <div key={albumName} className="bg-[#141414] border border-[#262626] overflow-hidden" data-testid={`free-album-${albumName}`}>
                <div className="flex flex-col sm:flex-row gap-6 p-6">
                  <div className="w-full sm:w-48 flex-shrink-0">
                    {tracks[0]?.cover_image ? (
                      <img src={toFullUrl(tracks[0].cover_image)} alt={albumName} className="w-full aspect-square object-cover border border-[#262626]" />
                    ) : (
                      <div className="w-full aspect-square bg-[#0A0A0A] border border-[#262626] flex items-center justify-center">
                        <Music className="w-12 h-12 text-[#262626]" />
                      </div>
                    )}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-baseline gap-3">
                      <span className="text-[#DAA520] font-mono text-xs font-bold">{String(idx + 1).padStart(2, '0')}</span>
                      <h3 className="font-cabinet text-2xl font-black uppercase">{albumName}</h3>
                    </div>
                    <div className="flex items-center gap-3 mt-1">
                      {tracks[0]?.artist && <p className="text-[#A3A3A3]">{tracks[0].artist}</p>}
                      {year && <span className="text-[#DAA520] text-sm font-mono">{year}</span>}
                    </div>
                    <p className="text-[#666] text-sm mt-1">{tracks.length} track{tracks.length > 1 ? 's' : ''}</p>
                    <div className="mt-4 divide-y divide-[#262626]">
                      {tracks.map((item, i) => (
                        <div key={item.id} className="flex items-center gap-3 py-2.5 group/track" data-testid={`free-media-${item.id}`}>
                          <span className="text-[#666] text-xs font-mono w-6 text-center flex-shrink-0">{item.track_number || i + 1}</span>
                          <button onClick={() => playFreeMedia(item)} className="w-7 h-7 flex items-center justify-center text-[#666] hover:text-[#DAA520] transition-colors flex-shrink-0" data-testid={`play-free-${item.id}`} title={item.audio_file ? 'Play' : 'No audio uploaded yet'}>
                            {currentTrack?.id === item.id && isPlaying ? <Pause className="w-4 h-4 text-[#DAA520]" /> : <Play className={`w-4 h-4 ${item.audio_file ? '' : 'opacity-30'}`} />}
                          </button>
                          <span className={`flex-1 truncate text-sm ${currentTrack?.id === item.id ? 'text-[#DAA520]' : ''}`}>{item.title}</span>
                          <span className="text-[10px] text-[#666] font-mono">{item.play_count || 0} plays</span>
                          {item.audio_file && (
                            <button onClick={() => queueFreeMedia(item)} className="text-[#666] hover:text-[#DAA520] transition-colors" title="Add to queue" data-testid={`queue-free-${item.id}`}>
                              <ListPlus className="w-4 h-4" />
                            </button>
                          )}
                          {item.is_downloadable && item.audio_file && (
                            <button onClick={() => downloadFreeMedia(item)} className="text-[#666] hover:text-[#DAA520] transition-colors" data-testid={`download-free-${item.id}`}>
                              <Download className="w-4 h-4" />
                            </button>
                          )}
                          {item.spotify_url && (
                            <a href={item.spotify_url} target="_blank" rel="noopener noreferrer" className="text-[#1DB954] hover:text-[#1ed760] transition-colors" title="Listen on Spotify">
                              <svg width="16" height="16" viewBox="0 0 24 24" fill="currentColor"><path d="M12 0C5.4 0 0 5.4 0 12s5.4 12 12 12 12-5.4 12-12S18.66 0 12 0zm5.521 17.34c-.24.359-.66.48-1.021.24-2.82-1.74-6.36-2.101-10.561-1.141-.418.122-.779-.179-.899-.539-.12-.421.18-.78.54-.9 4.56-1.021 8.52-.6 11.64 1.32.42.18.479.659.301 1.02zm1.44-3.3c-.301.42-.841.6-1.262.3-3.239-1.98-8.159-2.58-11.939-1.38-.479.12-1.02-.12-1.14-.6-.12-.48.12-1.021.6-1.141C9.6 9.9 15 10.561 18.72 12.84c.361.181.54.78.241 1.2zm.12-3.36C15.24 8.4 8.82 8.16 5.16 9.301c-.6.179-1.2-.181-1.38-.721-.18-.601.18-1.2.72-1.381 4.26-1.26 11.28-1.02 15.721 1.621.539.3.719 1.02.419 1.56-.299.421-1.02.599-1.559.3z"/></svg>
                            </a>
                          )}
                        </div>
                      ))}
                    </div>
                    <div className="flex items-center gap-4 mt-4">
                      <LikeButton itemType="track" itemId={tracks[0]?.id} />
                      <FireButton itemType="track" itemId={tracks[0]?.id} />
                    </div>
                    <CommentSection itemType="track" itemId={tracks[0]?.id} />
                  </div>
                </div>
              </div>
            );
          })}

          {/* Singles Grid */}
          {singles.length > 0 && (
            <div>
              {sortedAlbumNames.length > 0 && (
                <h2 className="font-cabinet text-xl font-black uppercase tracking-tighter mb-4 text-[#A3A3A3]">SINGLES</h2>
              )}
              <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
                {singles.map(item => (
                  <div key={item.id} className="bg-[#141414] border border-[#262626] overflow-hidden group" data-testid={`free-media-${item.id}`}>
                    <div className="aspect-square bg-[#0A0A0A] overflow-hidden relative">
                      {item.cover_image ? (
                        <img src={toFullUrl(item.cover_image)} alt={item.title} className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500" />
                      ) : (
                        <div className="w-full h-full flex items-center justify-center">
                          <Music className="w-12 h-12 text-[#262626]" />
                        </div>
                      )}
                    </div>
                    <div className="p-4">
                      <h3 className="font-cabinet font-bold uppercase truncate">{item.title}</h3>
                      {item.artist && <p className="text-[#A3A3A3] text-sm">{item.artist}</p>}
                      <div className="flex gap-2 mt-3">
                        {item.audio_file ? (
                          <Button onClick={() => playFreeMedia(item)} variant="outline"
                            className="flex-1 rounded-none border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520]"
                            data-testid={`play-free-${item.id}`}>
                            {currentTrack?.id === item.id && isPlaying ? <><Pause className="w-4 h-4 mr-2" /> PLAYING</> : <><Play className="w-4 h-4 mr-2" /> STREAM</>}
                          </Button>
                        ) : (
                          <Button disabled variant="outline"
                            className="flex-1 rounded-none border-[#262626] text-[#666] cursor-not-allowed"
                            title="No audio uploaded yet">
                            <Play className="w-4 h-4 mr-2 opacity-30" /> NO AUDIO
                          </Button>
                        )}
                        {item.audio_file && (
                          <Button onClick={() => queueFreeMedia(item)} variant="outline"
                            className="rounded-none border-[#262626] hover:border-[#DAA520] hover:text-[#DAA520]"
                            data-testid={`queue-free-${item.id}`} title="Add to queue">
                            <ListPlus className="w-4 h-4" />
                          </Button>
                        )}
                        {item.is_downloadable && item.audio_file && (
                          <Button onClick={() => downloadFreeMedia(item)}
                            className="rounded-none bg-[#DAA520] hover:bg-white hover:text-[#0A0A0A] text-black"
                            data-testid={`download-free-${item.id}`}>
                            <Download className="w-4 h-4" />
                          </Button>
                        )}
                      </div>
                      <div className="flex items-center gap-4 mt-3">
                        <LikeButton itemType="track" itemId={item.id} />
                        <FireButton itemType="track" itemId={item.id} />
                      </div>
                      <CommentSection itemType="track" itemId={item.id} />
                    </div>
                  </div>
                ))}
              </div>
            </div>
          )}

          {freeMedia.length === 0 && (
            <div className="bg-[#141414] border border-[#262626] p-12 text-center">
              <Music className="w-12 h-12 mx-auto text-[#262626] mb-4" />
              <p className="text-[#A3A3A3]">No free media available yet.</p>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
